const HEX_COLOR = /^#(?:[\da-f]{3}|[\da-f]{6})$/i

export const WHITE = '#ffffff'
export const BLACK = '#000000'

export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value.trim())
}

function toRgb(hex: string): [number, number, number] | undefined {
  const value = hex.trim()
  if (!isHexColor(value)) {
    return undefined
  }

  const digits = value.slice(1)
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map(d => d + d)
          .join('')
      : digits

  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16)
  ]
}

/** WCAG 2.1 relative luminance, 0 (black) to 1 (white). */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const [rl, gl, bl] = [r, g, b].map(channel => {
    const c = channel / 255
    return c <= 0.040_31 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl
}

/** WCAG 2.1 contrast ratio between two hex colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number | undefined {
  const first = toRgb(a)
  const second = toRgb(b)
  if (!first || !second) {
    return undefined
  }

  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second))
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second))

  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * WCAG 2.1 AA for large text and non-text UI. Deliberately not the 4.5 used for
 * body copy: the brand accent scores 3.95 against white, so demanding 4.5 would
 * flip Arena's own red buttons to black text.
 */
const MIN_CONTRAST = 3

/**
 * Black or white for text on `background`. White is the house style and is kept
 * unless it becomes unreadable, which is why this is not a plain "pick the
 * higher ratio" — black beats white on the brand red without being wanted there.
 */
export function readableTextColor(background: string): string {
  const onWhite = contrastRatio(background, WHITE)
  const onBlack = contrastRatio(background, BLACK)
  if (onWhite === undefined || onBlack === undefined) {
    return WHITE
  }

  return onWhite < MIN_CONTRAST && onBlack > onWhite ? BLACK : WHITE
}
